import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, Loader2, Maximize, Minimize, Monitor, MonitorX, RotateCw } from 'lucide-react'
import { getKasmSession, getDesktopOptions, type DesktopOs } from '../../services/api'

type ElectronClipboardApi = {
  writeFromDesktop: (text: string) => void
  onHostChange: (cb: (text: string) => void) => () => void
  setDesktopActive: (active: boolean) => void
}
function getElectronClipboard(): ElectronClipboardApi | undefined {
  if (typeof window === 'undefined') return undefined
  return (window as unknown as { electronAPI?: { clipboard?: ElectronClipboardApi } })
    .electronAPI?.clipboard
}

type State =
  | { phase: 'loading' }
  | { phase: 'ready'; url: string }
  | { phase: 'error'; message: string }

const OS_LABEL: Record<DesktopOs, string> = { linux: 'Linux', windows: 'Windows' }

/**
 * Personal remote desktop. Both Linux (the Fake10 desktop) and Windows (an
 * xfreerdp viewer into the user's VM) stream via KasmVNC.
 *
 * Controls live in a slim bar ABOVE the stream (never overlapping the remote
 * desktop's own window controls — the previous floating overlays sat on top of
 * the desktop and collided with its buttons). The Fullscreen button is a TOGGLE
 * and stays reachable in fullscreen, because the remote captures the keyboard so
 * Esc alone can't reliably bring you back out.
 */
export default function RemoteDesktop() {
  const [oses, setOses] = useState<DesktopOs[]>(['linux'])
  const [os, setOs] = useState<DesktopOs>('linux')
  const [state, setState] = useState<State>({ phase: 'loading' })
  const [isFs, setIsFs] = useState(false)
  const frameRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [synced, setSynced] = useState(false)
  // Loop guards + browser write-on-gesture. Refs (not state) so the message
  // listener reads current values without re-subscribing.
  const lastSentToDesktopRef = useRef<string | null>(null)
  const lastWrittenFromDesktopRef = useRef<string | null>(null)
  const frameOriginRef = useRef<string | null>(null)
  const loadGenerationRef = useRef(0)
  // Browser only: the latest desktop clipboard awaiting a user gesture on the OS
  // page (writeText can't run while the cross-origin iframe holds focus).
  const pendingHostWriteRef = useRef<string | null>(null)

  // Discover which OS choices this server offers (linux always; windows if granted).
  useEffect(() => {
    getDesktopOptions()
      .then((o) => {
        if (o.oses.length) setOses(o.oses)
      })
      .catch(() => {
        /* keep the linux default */
      })
  }, [])

  const load = useCallback((target: DesktopOs) => {
    const generation = ++loadGenerationRef.current
    frameOriginRef.current = null
    pendingHostWriteRef.current = null
    lastWrittenFromDesktopRef.current = null
    lastSentToDesktopRef.current = null
    setSynced(false)
    setState({ phase: 'loading' })
    getKasmSession(target)
      .then(({ url }) => {
        if (generation !== loadGenerationRef.current) return
        frameOriginRef.current = new URL(url).origin
        setState({ phase: 'ready', url })
      })
      .catch((err) => {
        if (generation !== loadGenerationRef.current) return
        const message = err?.response?.data?.error ?? 'Could not start the desktop. Try again.'
        setState({ phase: 'error', message })
      })
  }, [])

  useEffect(() => () => {
    loadGenerationRef.current += 1
  }, [])

  useEffect(() => {
    load(os)
  }, [load, os])

  // Mirror the real fullscreen state (Esc / browser chrome can also exit) so the
  // toggle always shows the correct action.
  useEffect(() => {
    const onChange = () => setIsFs(document.fullscreenElement === frameRef.current)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {})
    } else {
      frameRef.current?.requestFullscreen?.().catch(() => {})
    }
  }

  // Host clipboard -> remote desktop is handled automatically by KasmVNC's own
  // clipboard sync now that the bridge forces non-seamless mode (see
  // docker/desktops/kasm-bridge/clipboardInject.js) — no "Paste" button needed.

  // Desktop clipboard -> host. The bridge-injected script auto-pushes the desktop
  // clipboard (rx2go-clipboard-out) whenever it changes. Origin-pinned listener.
  useEffect(() => {
    const electronClipboard = getElectronClipboard()
    const onMessage = (e: MessageEvent) => {
      if (!frameOriginRef.current || e.origin !== frameOriginRef.current) return
      if (!iframeRef.current?.contentWindow || e.source !== iframeRef.current.contentWindow) return
      const d = e.data as { type?: string; text?: string } | null
      if (!d || d.type !== 'rx2go-clipboard-out' || typeof d.text !== 'string') return
      const text = d.text
      if (!text) return
      // Loop guard: ignore the echo of what we just pasted INTO the desktop.
      if (text === lastSentToDesktopRef.current) return

      if (electronClipboard) {
        electronClipboard.writeFromDesktop(text)
        lastWrittenFromDesktopRef.current = text
        return
      }
      // Browser: writeText needs focus + a user gesture on THIS (top) page, which
      // a copy inside the cross-origin iframe doesn't provide. Stash the latest
      // desktop clipboard; the gesture-flush effect writes it on the next OS-page
      // interaction.
      pendingHostWriteRef.current = text
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  // Browser only: flush the stashed desktop clipboard to the host on the next user
  // gesture on the OS page (writeText needs focus + activation). Electron writes
  // immediately in the listener above, so it skips this.
  useEffect(() => {
    if (getElectronClipboard()) return
    const flush = () => {
      const text = pendingHostWriteRef.current
      if (!text || text === lastWrittenFromDesktopRef.current) return
      const cb = navigator.clipboard
      if (!cb?.writeText) return
      cb.writeText(text).then(
        () => {
          lastWrittenFromDesktopRef.current = text
          pendingHostWriteRef.current = null
          setSynced(true)
          window.setTimeout(() => setSynced(false), 1500)
        },
        () => { /* not focused / denied — keep pending, retry on the next gesture */ },
      )
    }
    window.addEventListener('pointerdown', flush, true)
    window.addEventListener('keydown', flush, true)
    window.addEventListener('focus', flush)
    return () => {
      window.removeEventListener('pointerdown', flush, true)
      window.removeEventListener('keydown', flush, true)
      window.removeEventListener('focus', flush)
    }
  }, [])

  // Electron only: poll the OS clipboard only while the desktop stream is up.
  useEffect(() => {
    if (state.phase !== 'ready') return
    const electronClipboard = getElectronClipboard()
    if (!electronClipboard) return
    electronClipboard.setDesktopActive(true)
    return () => electronClipboard.setDesktopActive(false)
  }, [state])

  // Electron only: host clipboard changed -> push it into the desktop frame.
  useEffect(() => {
    if (state.phase !== 'ready') return
    const electronClipboard = getElectronClipboard()
    if (!electronClipboard) return
    const frameOrigin = new URL(state.url).origin
    const off = electronClipboard.onHostChange((text) => {
      if (!text || text === lastWrittenFromDesktopRef.current) return
      try {
        iframeRef.current?.contentWindow?.postMessage({ type: 'rx2go-clipboard', text }, frameOrigin)
        lastSentToDesktopRef.current = text
      } catch {
        /* ignore */
      }
    })
    return off
  }, [state])

  return (
    <div ref={frameRef} className="flex-1 h-full min-h-0 flex flex-col bg-black">
      {/* Control bar — above the stream, so it never overlaps the desktop's UI. */}
      <div className="shrink-0 h-9 px-2 flex items-center justify-between gap-2 border-b border-border bg-card">
        <div className="flex items-center gap-1">
          {oses.length > 1 &&
            oses.map((o) => (
              <button
                key={o}
                onClick={() => setOs(o)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold transition duration-150 ${
                  o === os
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Monitor size={12} /> {OS_LABEL[o]}
              </button>
            ))}
        </div>
        <div className="flex items-center gap-1">
          {synced && (
            <span
              title="The desktop's clipboard was copied to your computer"
              className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold text-muted-foreground"
            >
              <Check size={12} /> Clipboard synced
            </span>
          )}
          <button
            onClick={toggleFullscreen}
            title={isFs ? 'Exit fullscreen' : 'Fullscreen'}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition duration-150"
          >
            {isFs ? <Minimize size={12} /> : <Maximize size={12} />}
            {isFs ? 'Exit fullscreen' : 'Fullscreen'}
          </button>
        </div>
      </div>

      {state.phase === 'loading' ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-muted-foreground bg-background">
          <Loader2 size={28} className="animate-spin" />
          <div className="text-sm">Starting {OS_LABEL[os]} desktop… first open can take ~30s</div>
        </div>
      ) : state.phase === 'error' ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 bg-background">
          <MonitorX size={28} className="text-muted-foreground" />
          <div className="text-sm text-muted-foreground">{state.message}</div>
          <button
            onClick={() => load(os)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold transition duration-150 hover:opacity-90"
          >
            <RotateCw size={13} /> Retry
          </button>
        </div>
      ) : (
        <iframe
          ref={iframeRef}
          src={state.url}
          title="Remote desktop"
          className="flex-1 w-full border-0"
          allow="clipboard-read; clipboard-write; microphone; fullscreen; keyboard-map"
        />
      )}
    </div>
  )
}
